import { useCallback, useState } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useConfig, useConnection, useSendTransaction, useSwitchChain } from 'wagmi'
import { waitForTransactionReceipt } from 'wagmi/actions'
import type { UnsignedTx } from '@algoboo/schema/api'
import { AddressLink, Button, Icon, Spinner, cx, type ButtonProps } from '@algoboo/ui'
import { useSession } from './session'
import { attempt } from '../lib/attempt'

export type TxPhase = 'idle' | 'preparing' | 'wallet' | 'pending' | 'confirmed' | 'indexed' | 'error'

export interface TxButtonProps {
  /** the unsigned transaction, or a function that fetches it from the API */
  tx: UnsignedTx | (() => Promise<UnsignedTx | undefined>)
  label: string
  /** queries to refresh once the receipt is in (the indexer then catches up) */
  invalidate?: readonly QueryKey[]
  /** poll until the API reflects the change (e.g. a token becomes live) */
  isIndexed?: () => boolean | Promise<boolean>
  onDone?: (hash: `0x${string}`) => void
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  icon?: ButtonProps['icon']
  disabled?: boolean
  className?: string
  /** explain the action for the sign-in prompt */
  reason?: string
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Sends an API-built `UnsignedTx` from the connected wallet and shows its
 * life inline: wallet → pending → confirmed → indexed.
 */
export function TxButton({ tx, label, invalidate, isIndexed, onDone, variant = 'primary', size = 'md', icon = 'chain', disabled, className, reason }: TxButtonProps) {
  const [phase, setPhase] = useState<TxPhase>('idle')
  const [hash, setHash] = useState<`0x${string}` | null>(null)
  const [error, setError] = useState<string | null>(null)
  const conn = useConnection()
  const config = useConfig()
  const { sendTransactionAsync } = useSendTransaction()
  const { switchChainAsync } = useSwitchChain()
  const qc = useQueryClient()
  const { me, signIn } = useSession()

  const run = useCallback(async () => {
    setError(null)
    setHash(null)
    await attempt(
      async () => {
        setPhase('preparing')
        const t = typeof tx === 'function' ? await tx() : tx
        if (!t) {
          setPhase('idle')
          return
        }
        setPhase('wallet')
        if (!conn.address) throw new Error('Connect a wallet first')
        if (conn.chainId !== t.chainId) await switchChainAsync({ chainId: t.chainId as never })
        const h = await sendTransactionAsync({
          to: t.to,
          data: t.data,
          value: BigInt(t.value),
          chainId: t.chainId as never,
          ...(t.gas ? { gas: BigInt(t.gas) } : {}),
        })
        setHash(h)
        setPhase('pending')
        const receipt = await waitForTransactionReceipt(config, { hash: h, chainId: t.chainId as never })
        if (receipt.status !== 'success') throw new Error('The transaction reverted on chain')
        setPhase('confirmed')
        await Promise.all((invalidate ?? []).map((queryKey) => qc.invalidateQueries({ queryKey })))
        if (isIndexed) {
          for (let i = 0; i < 30; i++) {
            if (await isIndexed()) break
            await sleep(1000)
            await Promise.all((invalidate ?? []).map((queryKey) => qc.invalidateQueries({ queryKey })))
          }
        }
        setPhase('indexed')
        onDone?.(h)
      },
      (err) => {
        const e = err as { shortMessage?: string; message?: string; name?: string }
        const rejected = e.name === 'UserRejectedRequestError' || /reject|denied/i.test(e.message ?? '')
        setError(rejected ? 'You declined in your wallet.' : (e.shortMessage ?? e.message ?? 'Transaction failed'))
        setPhase('error')
      },
    )
  }, [tx, conn.address, conn.chainId, config, sendTransactionAsync, switchChainAsync, invalidate, isIndexed, qc, onDone])

  const busy = phase === 'preparing' || phase === 'wallet' || phase === 'pending' || phase === 'confirmed'
  const onClick = () => {
    if (!me) {
      signIn(reason ?? `Sign in to ${label.toLowerCase()}.`)
      return
    }
    void run()
  }

  return (
    <span className={cx('inline-flex flex-wrap items-center gap-2', className)} data-testid="tx-button" data-phase={phase} data-label={label} {...(hash ? { 'data-tx-hash': hash } : {})}>
      {phase === 'indexed' ? (
        <span className="inline-flex h-9 items-center gap-1.5 rounded-[8px] border border-line-strong px-3.5 text-[13.5px] font-medium text-gain">
          <Icon name="check" size={14} strokeWidth={2.4} /> Done
        </span>
      ) : (
        <Button variant={variant} size={size} icon={icon} loading={busy} disabled={disabled || busy} onClick={onClick}>
          {label}
        </Button>
      )}
      <span aria-live="polite" className="inline-flex items-center gap-1.5 text-[12px] text-ink-3">
        {phase === 'preparing' && 'Preparing transaction…'}
        {phase === 'wallet' && 'Confirm in your wallet…'}
        {phase === 'pending' && (
          <>
            <Spinner size={11} /> Pending {hash && <AddressLink kind="tx" value={hash} copy={false} />}
          </>
        )}
        {phase === 'confirmed' && (
          <>
            <Icon name="check" size={12} /> Confirmed, waiting for algo.boo to record it…
          </>
        )}
        {phase === 'indexed' && hash && <AddressLink kind="tx" value={hash} copy={false} />}
        {phase === 'error' && <span className="font-medium text-danger">{error}</span>}
      </span>
    </span>
  )
}
